import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentLayout } from 'components/Layouts'
import { MyLink, MyInternalLink } from 'components/MyLink'
import { PremiumBillCard } from '../../components/PremiumBillCard/PremiumBillCard'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { Card } from '@cmsgov/ds-medicare-gov'
import { useFetchPremiumBills } from 'queries/premiums'
import { Alert } from '@cmsgov/design-system'
import { BackToPremiumsLink } from 'containers/Premiums/components/BackToPremiums'
import { BackToPremiumsBreadcrumb } from 'containers/Premiums/components/BackToPremiumsBreadcrumb'
import { BillsNoResultsAlert } from 'containers/Premiums/components/bills/BillsNoResultsAlert'
import { useEvent } from 'hooks/useEvent'
import { Component } from '@cms-max/medicare-api-sdk'

export const PremiumsBills: FC = () => {
  const { t } = useTranslation('premiumsBills')
  const { data, isError, isLoading, isSuccess } = useFetchPremiumBills()

  const premiumBillResults = data ?? []
  const hasPremiumBillResults = isSuccess && premiumBillResults.length > 0
  const noPremiumBillResults = isSuccess && premiumBillResults.length === 0
  const mspLink = useMedicareUrl(
    'https://www.medicare.gov/your-medicare-costs/get-help-paying-costs/medicare-savings-programs',
    'https://es.medicare.gov/your-medicare-costs/get-help-paying-costs/medicare-savings-programs'
  )
  const premiumsRootPath = '/premiums/dashboard'
  const payNowPath = '/premiums/make-a-payment'

  const { fireEvent } = useEvent('premiumBills')
  return (
    <>
      <ContentLayout
        className="ds-u-padding-bottom--7"
        heading={t('premiumsBillsHeading')}
        width="wide"
        withPrintButton
        printComponent={Component.Premiums}
        nav={<BackToPremiumsBreadcrumb />}
      >
        <div className="ds-l-container">
          {isError && (
            <>
              <Alert className="ds-u-measure--wide" variation='error' heading={t('errorAlert.heading')}>{t('errorAlert.content')}</Alert>
              <div className="ds-u-margin-top--3">
                <BackToPremiumsLink />
              </div>
            </>
          )}
          {noPremiumBillResults && !isLoading && (
            <>
              <BillsNoResultsAlert className='ds-u-margin-bottom--3' />
              <BackToPremiumsLink />
            </>
          )}

          {!isError && !noPremiumBillResults && (
            <>
              <div className='ds-l-row'>
                <div className='ds-l-col--12'>
                  <p className='ds-u-margin--0'>
                    {t('premiumBillsIntro')}
                    <MyLink
                      href={mspLink}
                      target='_blank'
                      onClick={params => fireEvent('premiums_premiumbills_learnaboutprograms_click', params)}
                    >
                      {t('premiumBillsIntroLink')}
                    </MyLink>
                  </p>
                </div>
              </div>
              <div className='ds-l-row ds-u-margin-y--2'>
                <div className='ds-l-col--12'>
                  {isLoading && (
                    <MySpinner />
                  )}
                  {hasPremiumBillResults && premiumBillResults.map(b => (
                    <PremiumBillCard bill={b} className='ds-u-margin-y--2' key={`premiumBill${b.lastBillDate}`}></PremiumBillCard>
                  ))}

                </div>
              </div>
              <div className='ds-l-row'>
                <div className='ds-l-col--12'>
                  <Card className='ds-u-flex-direction--column ds-u-md-flex-direction--row'>
                    <div className='ds-u-padding--3'>
                      <div className='ds-l-row'>
                        <div className='ds-l-col--12'>
                          {t('makeAPaymentText')}
                        </div>
                      </div>
                      <div className='ds-l-row ds-u-margin-top--2'>
                        <div className='ds-l-col--auto'>
                          <MyInternalLink
                            to={payNowPath}
                            className='ds-c-button ds-c-button--solid ds-u-padding-x--5'
                            onClick={params => fireEvent('premiums_premiumbills_paynow_click', params)}
                          >
                            {t('payNowButton')}
                          </MyInternalLink>
                        </div>
                        <div className='ds-l-col--auto ds-u-padding-top--1'>
                          <MyInternalLink
                            to={premiumsRootPath}
                            onClick={params => fireEvent('premiums_premiumbills_backtopremiums_click', params)}
                          >
                            {t('backToPremiumsOverviewLink')}
                          </MyInternalLink>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </>
          )}
        </div>
      </ContentLayout>
    </>
  )
}
